import Dialog from '../../../components/Dialog';
import { IconWarn } from '../../../components/dialogIcons';
import React, { useRef, useState } from 'react';
import { clock } from '../../../lib/when';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { DESKTOP_QUERY } from '../../../components/PageBar';
import { Card, Button } from './movementUi';
import EmptyScreen from '../../../components/EmptyScreen';
import { fileToDataUrl, ocrImage } from './ocrClient';
import { startRun, uploadPage, markPageRead, markRunReading, saveRunLines } from './movementRuns';
import { buildReviewRows, outcomeFor, suggestWarehouse } from './movementUtils';
import { pressable } from '../../../lib/pressable';

// Upload handwritten pages, OCR them, then resolve any unknown account
// shorthands one at a time before handing off to Review.
export default function ProcessTab({
  warehouses, mappings, setMappings,
  images, setImages, setExtractedPages, onProcessed, toast,
  shipsFrom, profile, onRunStarted,
}) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [log, setLog] = useState([]);
  const [unknownQueue, setUnknownQueue] = useState([]);
  const [modalChoice, setModalChoice] = useState('');
  const pagesRef = useRef([]);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const isDesktop = useMediaQuery(DESKTOP_QUERY);

  const logMsg = (msg, type = 'info') => setLog(prev => [...prev, { msg, type, at: clock(new Date()) }]);

  async function addFiles(fileList) {
    const files = Array.from(fileList || []).filter(f => /^image\//.test(f.type) || /\.(jpe?g|png|jfif|webp|gif)$/i.test(f.name));
    if (!files.length) return;
    const added = [];
    /*
      **The File itself is kept beside the picture now.** Until 8 September 2026
      only the data URL was, because nothing was ever uploaded: the browser
      turned the photograph into text in memory, posted it to the reader and
      forgot it. The owner asked on 5 September for the pages to be uploaded,
      read, reviewed and then deleted, and none of that can happen to a string.
    */
    for (const f of files) added.push({ name: f.name, file: f, dataUrl: await fileToDataUrl(f) });
    setImages(prev => [...prev, ...added]);
  }

  async function process() {
    if (!images.length) { toast.error('Add at least one page image.'); return; }

    setRunning(true);
    setLog([]);
    setProgress({ done: 0, total: images.length });

    /*
      **A run exists before the first page is read.** Everything this tool does
      now hangs off it: the uploaded pages, the lines, and the record that stays
      behind after the photographs are deleted.

      **If the run cannot be started, the reading still happens.** Reading a
      page costs money and a person's morning; refusing to do it because the
      history could not be written would be the tail wagging the dog. What is
      lost is the record, and the log says so rather than staying quiet, which
      is the whole of "stop the silent failures".
    */
    let run = null;
    const { run: made, error: runErr } = await startRun({ shipsFrom, profile });
    if (runErr) {
      logMsg(`Could not start a run record: ${runErr.message}. The pages will still be read, but this run will not appear in Run History.`, 'warn');
    } else {
      run = made;
      if (onRunStarted) onRunStarted(run);
      logMsg(`Run ${run.name} started.`);
    }

    const pages = [];
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      logMsg(`Reading ${img.name} (${i + 1}/${images.length})...`);

      // The photograph goes up before it is read, so a reading that fails still
      // leaves the page it failed on where somebody can look at it.
      let pageRow = null;
      if (run && img.file) {
        const { page, error } = await uploadPage({ run, file: img.file, pageNo: i + 1 });
        if (error) logMsg(`  ${img.name}: could not be uploaded (${error.message}). It is still read.`, 'warn');
        else pageRow = page;
      }

      try {
        const result = await ocrImage(img);
        const sections = result.sections || [];
        const itemCount = sections.reduce((n, s) => n + (s.items || []).length, 0);
        pages.push({ ...result, imageName: img.name, pageId: pageRow?.id || null });
        if (pageRow) await markPageRead(pageRow.id, itemCount);
        logMsg(`  ${img.name}: ${sections.length} account${sections.length === 1 ? '' : 's'}, ${itemCount} item${itemCount === 1 ? '' : 's'}`, 'ok');
      } catch (e) {
        logMsg(`  ${img.name}: ${e.message}`, 'err');
      }
      setProgress({ done: i + 1, total: images.length });
    }

    if (run) {
      await markRunReading(run.id, pages.length);
      /*
        **The lines are written down the moment they are read**, not when the
        file is made. Between those two is where somebody goes to lunch, and
        until this was here the whole morning still lived in one browser tab.
        Reading a page again costs about three cents and a person's patience.
      */
      const draft = buildReviewRows(pages, mappings).map(r => {
        const { outcome, why } = outcomeFor(r);
        return {
          pageId: r.pageId || null,
          written: r.shorthand || '',
          // The date at the top of the page. It is the only record of WHEN the
          // stock moved and it goes into the file's Comments column, so a run
          // picked back up without it makes a file with no dates in it.
          date: r.date || '',
          destination: r.destination || '',
          itemNumber: r.itemNumber || '',
          description: '',
          quantity: r.quantity,
          unit: r.unit || '',
          outcome, why,
        };
      });
      const { error } = await saveRunLines({ run, rows: draft });
      if (error) logMsg(`  The lines could not be saved: ${error.message}. They are still on this screen.`, 'warn');
      else logMsg(`  ${draft.length} line${draft.length === 1 ? '' : 's'} saved. This run survives a closed tab now.`, 'ok');
    }

    pagesRef.current = pages;
    setExtractedPages(pages);
    setRunning(false);

    if (!pages.length) { toast.error('No pages could be read.'); return; }

    // Every shorthand we have not mapped yet gets resolved before Review.
    const seen = [];
    for (const p of pages) {
      for (const s of p.sections || []) {
        const sh = s.shorthand || '';
        if (sh && !mappings[sh] && !seen.includes(sh)) seen.push(sh);
      }
    }
    if (seen.length) {
      logMsg(`${seen.length} account${seen.length === 1 ? '' : 's'} need mapping.`, 'warn');
      setUnknownQueue(seen);
      setModalChoice(suggestWarehouse(seen[0], warehouses) || '');
    } else {
      onProcessed(pages, mappings);
      toast.success('Pages read. Review is ready.');
    }
  }

  function advanceQueue(nextMappings) {
    const rest = unknownQueue.slice(1);
    setUnknownQueue(rest);
    if (rest.length) {
      setModalChoice(suggestWarehouse(rest[0], warehouses) || '');
    } else {
      onProcessed(pagesRef.current, nextMappings);
      toast.success('Pages read. Review is ready.');
    }
  }

  function saveMappingAndContinue() {
    const sh = unknownQueue[0];
    const next = modalChoice ? { ...mappings, [sh]: modalChoice } : mappings;
    if (modalChoice) setMappings(next);
    advanceQueue(next);
  }

  const current = unknownQueue[0];
  const suggestion = current ? suggestWarehouse(current, warehouses) : null;

  const pickers = (
    <>
      <input ref={fileRef} type="file" accept="image/*,.jfif" multiple style={{ display: 'none' }}
        onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
        onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
    </>
  );

  /*
    **The empty version is the primary drawing.** With no run picked up, this
    screen meets a person with nothing on it, every time. Group G drew it as a
    whole screen with the two acts on it rather than as a note under a card, and
    the acts are the point: **the camera is the biggest single change in the
    group** and it belongs where somebody first lands, not behind a card header.

    Rule 21 usually says do not leave something out at a size because of the
    device. Here the device argument runs the other way: the camera is offered
    below 1024 and not on a desk, because a second button opening the same file
    picker would be a lie about what it does.
  */
  if (!images.length && !log.length) {
    return (
      <>
        {pickers}
        <EmptyScreen
          drop
          title="No pages yet"
          says={'Photograph each handwritten movement list, or drop photos you already have. '
              + 'OnHand reads them and builds the list you check.'}
        >
          <div className="mv-acts">
            {!isDesktop && (
              <button className="btn btn-primary" onClick={() => cameraRef.current?.click()}>
                Take a photo
              </button>
            )}
            <button className={'btn ' + (isDesktop ? 'btn-primary' : 'btn-outline')}
              onClick={() => fileRef.current?.click()}>
              Choose photos
            </button>
          </div>
        </EmptyScreen>
      </>
    );
  }

  /* Full width, like every other screen in the group. */
  return (
    <div>
      <Card
        title="Page images"
        sub="Photos of the handwritten movement lists. JPG, PNG, or JFIF."
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            {/*
              **The camera, added 5 September 2026.**

              Reading a movement page starts with photographing it, and the phone
              is the thing with the camera. This screen had no camera button on
              any device: a file picker and a drop area, and nothing else. So the
              one step that has to happen on a phone could only be started by
              photographing the page with something else, sending yourself the
              file, and picking it up on a laptop.

              **Rule 21 usually says do not leave something out at a size because
              of the device. Here the device argument runs the other way**: the
              button is offered below 1024 and not on a desk, because `capture`
              means nothing to a machine with no camera and a second button that
              opens the same file picker would be a lie about what it does.
            */}
            {!isDesktop && <Button onClick={() => cameraRef.current?.click()}>Take a photo</Button>}
            <Button variant="ghost" onClick={() => fileRef.current?.click()}>Choose photos</Button>
            {images.length > 0 && <Button variant="quiet" onClick={() => setImages([])}>Clear</Button>}
          </div>
        }
      >
        {pickers}
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          style={{ border: '1.5px dashed var(--border)', borderRadius: 12, padding: images.length ? 14 : 34, textAlign: images.length ? 'left' : 'center' }}
        >
          {images.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{isDesktop ? 'Drop page photos here, or use "Choose photos".' : 'Take a photo of each page, or add ones you already have.'}</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {images.map((img, i) => (
                <div key={img.name + i} style={{ border: '1px solid var(--border)', borderRadius: 'var(--oh-radius-control)', overflow: 'hidden', background: 'var(--surface-card)' }}>
                  <img src={img.dataUrl} alt={img.name} style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                  <div style={{ padding: '7px 9px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.name}</span>
                    <span
                      {...pressable(() => setImages(prev => prev.filter((_, idx) => idx !== i)))}
                      style={{ fontSize: 11, fontWeight: 700, color: 'var(--critical-ink)', cursor: 'pointer' }}
                    >
                      Remove
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <Button onClick={process} disabled={running || !images.length}>
            {running ? `Reading ${progress.done}/${progress.total}...` : 'Read pages'}
          </Button>
          {running && (
            <div style={{ flex: 1, height: 8, borderRadius: 'var(--oh-radius-pill)', background: 'var(--border-soft)', overflow: 'hidden' }}>
              <div style={{ width: (progress.total ? (progress.done / progress.total) * 100 : 0) + '%', height: '100%', background: 'var(--action)', transition: 'width 0.2s' }} />
            </div>
          )}
        </div>
      </Card>

      {log.length > 0 && (
        <Card title="Run log">
          <div style={{ background: 'var(--hover)', borderRadius: 'var(--oh-radius-control)', padding: 12, maxHeight: 240, overflowY: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11.5, lineHeight: 1.7 }}>
            {log.map((l, i) => (
              <div key={i} style={{ color: l.type === 'err' ? 'var(--critical-ink)' : l.type === 'ok' ? 'var(--success-ink)' : l.type === 'warn' ? 'var(--warning-ink)' : 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{l.at}</span>  {l.msg}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Unknown-shorthand resolution, one account at a time.

          **The eighteenth dialog, and nobody had counted it.** Group E counted
          seventeen from the counting screen and the shared components; this one
          sits on the movement screen, was hand built out of two divs, and was
          the only dialog in OnHand with no way to close it but answering it. */}
      {current && (
        <Dialog
          kind="question" icon={<IconWarn />}
          title="Unknown account"
          record={current}
          says={unknownQueue.length + ' left to map. What you pick is saved for next time.'}
          onClose={() => advanceQueue(mappings)}
          footer={
            <>
              <Button variant="quiet" onClick={() => advanceQueue(mappings)}>Skip this account</Button>
              <Button onClick={saveMappingAndContinue}>Save and continue</Button>
            </>
          }
        >
          {suggestion && (
            <div style={{ fontSize: 12.5, color: 'var(--success-ink)', fontWeight: 700, marginBottom: 10 }}>Suggested: {suggestion}</div>
          )}
          <select className="form-sel" value={modalChoice} onChange={e => setModalChoice(e.target.value)}>
            <option value="">Skip, do not map</option>
            {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </Dialog>
      )}
    </div>
  );
}
